import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Button, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import {
    SwiftFlowError,
    SwiftFlowErrorType,
    useCreateSwiftFlowErrorMutation,
    useGetSwiftFlowErrorsQuery,
    useUpdateSwiftFlowErrorMutation,
} from '@/services/api';
import {
    importSwiftFlowErrorsCsv,
    ImportEvent,
    ImportSummary,
} from '@/features/swiftFlowErrors/importSwiftFlowErrorsCsv';
import { SwiftFlowErrorsHeader } from '@/features/swiftFlowErrors/SwiftFlowErrorsHeader';
import { SwiftFlowErrorsFilters } from '@/features/swiftFlowErrors/SwiftFlowErrorsFilters';
import { SwiftFlowErrorsTable } from '@/features/swiftFlowErrors/SwiftFlowErrorsTable';
import { EditSwiftFlowErrorDialog } from '@/features/swiftFlowErrors/EditSwiftFlowErrorDialog';
import { CreateSwiftFlowErrorDialog } from '@/features/swiftFlowErrors/CreateSwiftFlowErrorDialog';
import { BulkUploadCsvDialog } from '@/features/swiftFlowErrors/BulkUploadCsvDialog';

interface EditState {
    open: boolean;
    error: SwiftFlowError | null;
    unifiedMessage: string;
    errorType: SwiftFlowErrorType;
    notify: boolean;
}

export default function SwiftFlowErrorsPage() {
    const { enqueueSnackbar } = useSnackbar();
    const { data, isLoading, isError, refetch, isFetching } = useGetSwiftFlowErrorsQuery();
    const [updateSwiftFlowError, { isLoading: isSaving }] = useUpdateSwiftFlowErrorMutation();
    const [createSwiftFlowError, { isLoading: isCreating }] = useCreateSwiftFlowErrorMutation();

    const jwtToken = useSelector((state: RootState) => state.user.jwtToken) ||
        (typeof window !== 'undefined' ? localStorage.getItem('token') : null);

    const [allErrors, setAllErrors] = useState<SwiftFlowError[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [gatewayFilter, setGatewayFilter] = useState<'all' | string>('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [editState, setEditState] = useState<EditState | null>(null);

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [createOriginalMessage, setCreateOriginalMessage] = useState('');
    const [createCode, setCreateCode] = useState('');
    const [createGateway, setCreateGateway] = useState('');
    const [createUnifiedMessage, setCreateUnifiedMessage] = useState('');
    const [createErrorType, setCreateErrorType] = useState<SwiftFlowErrorType>('None');
    const [createNotify, setCreateNotify] = useState(false);

    const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
    const [csvContent, setCsvContent] = useState('');
    const [isImporting, setIsImporting] = useState(false);
    const [importLog, setImportLog] = useState<ImportEvent[]>([]);
    const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
    const [importAbortController, setImportAbortController] = useState<AbortController | null>(null);

    useEffect(() => {
        if (data?.data) {
            setAllErrors(data.data);
        }
    }, [data]);

    const gateways = useMemo(() => {
        const set = new Set<string>();
        allErrors.forEach((e) => {
            if (e.gateway) {
                set.add(e.gateway);
            }
        });
        return Array.from(set).sort();
    }, [allErrors]);

    const filteredErrors = useMemo(() => {
        let result = allErrors;

        if (searchTerm.trim()) {
            const term = searchTerm.trim().toLowerCase();
            result = result.filter((e) =>
                e.swiftFlowErrorCode.toLowerCase().includes(term) ||
                e.originalErrorMessage.toLowerCase().includes(term),
            );
        }

        if (gatewayFilter !== 'all') {
            result = result.filter((e) => e.gateway === gatewayFilter);
        }

        return result;
    }, [allErrors, searchTerm, gatewayFilter]);

    const handleOpenEdit = (error: SwiftFlowError) => {
        setEditState({
            open: true,
            error,
            unifiedMessage: error.swiftFlowUnifiedMessage || '',
            errorType: (error.errorType as SwiftFlowErrorType) || 'None',
            notify: Boolean(error.notify),
        });
    };

    const handleOpenCreateDialog = () => {
        setCreateOriginalMessage('');
        setCreateCode('');
        setCreateGateway('');
        setCreateUnifiedMessage('');
        setCreateErrorType('None');
        setCreateNotify(false);
        setCreateDialogOpen(true);
    };

    const handleCloseCreateDialog = () => {
        if (isCreating) return;
        setCreateDialogOpen(false);
    };

    const handleCreateSubmit = async () => {
        if (!createOriginalMessage.trim() || !createCode.trim() || !createUnifiedMessage.trim()) {
            enqueueSnackbar('Original message, error code, and unified message are required', { variant: 'error' });
            return;
        }

        try {
            const created = await createSwiftFlowError({
                originalErrorMessage: createOriginalMessage.trim(),
                swiftFlowErrorCode: createCode.trim(),
                gateway: createGateway.trim() || undefined,
                swiftFlowUnifiedMessage: createUnifiedMessage.trim(),
                errorType: createErrorType === 'None' ? undefined : createErrorType,
                notify: createNotify,
            }).unwrap();

            setAllErrors((prev) => [{ ...created }, ...prev]);
            enqueueSnackbar('Swift Flow error created successfully', { variant: 'success' });
            setCreateDialogOpen(false);
        } catch (error: any) {
            const message = error?.data?.message || 'Failed to create Swift Flow error';
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    const handleCloseEdit = () => {
        setEditState(null);
    };

    const handleSave = async () => {
        if (!editState || !editState.error) return;

        if (!editState.unifiedMessage.trim()) {
            enqueueSnackbar('Unified message is required', { variant: 'error' });
            return;
        }

        try {
            const payload = {
                swiftFlowUnifiedMessage: editState.unifiedMessage.trim(),
                errorType: editState.errorType === 'None' ? undefined : editState.errorType,
                notify: editState.notify,
            };

            const updated = await updateSwiftFlowError({ id: editState.error.id, payload }).unwrap();

            setAllErrors((prev) =>
                prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)),
            );

            enqueueSnackbar('Swift Flow error updated successfully', { variant: 'success' });
            handleCloseEdit();
        } catch (error: any) {
            const message = error?.data?.message || 'Failed to update Swift Flow error';
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenBulkDialog = () => {
        setBulkDialogOpen(true);
        setImportLog([]);
        setImportSummary(null);
    };

    const handleCloseBulkDialog = () => {
        if (isImporting && importAbortController) {
            importAbortController.abort();
        }
        setIsImporting(false);
        setImportAbortController(null);
        setBulkDialogOpen(false);
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            const text = await file.text();
            setCsvContent(text);
        } catch (err) {
            enqueueSnackbar('Failed to read CSV file', { variant: 'error' });
        }
    };

    const handleStartImport = async () => {
        if (!csvContent.trim()) {
            enqueueSnackbar('Please paste CSV content before starting import', { variant: 'error' });
            return;
        }

        setIsImporting(true);
        setImportLog([]);
        setImportSummary(null);

        const controller = new AbortController();
        setImportAbortController(controller);

        await importSwiftFlowErrorsCsv(csvContent, jwtToken, {
            signal: controller.signal,
            onRow: (event) => {
                setImportLog((prev) => [...prev, event]);
            },
            onDone: (summary) => {
                setImportSummary(summary);
                setIsImporting(false);
                setImportAbortController(null);
                refetch();
            },
            onError: (err) => {
                if ((err as any)?.name === 'AbortError') {
                    setIsImporting(false);
                    setImportAbortController(null);
                    return;
                }
                // eslint-disable-next-line no-console
                console.error('Import error', err);
                enqueueSnackbar('Failed to import CSV. Please try again.', { variant: 'error' });
                setIsImporting(false);
                setImportAbortController(null);
            },
        });
    };

    if (isLoading && !data) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box sx={{ mt: 4 }}>
                <Typography color="error" align="center">
                    Failed to load Swift Flow Errors. Please try again.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button variant="outlined" onClick={() => refetch()}>
                        Retry
                    </Button>
                </Box>
            </Box>
        );
    }

    const paginated = filteredErrors.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
    );

    return (
        <Box sx={{ width: '100%' }}>
            <SwiftFlowErrorsHeader
                isFetching={isFetching}
                isCreating={isCreating}
                onNewError={handleOpenCreateDialog}
                onBulkUpload={handleOpenBulkDialog}
            />

            <SwiftFlowErrorsFilters
                searchTerm={searchTerm}
                gatewayFilter={gatewayFilter}
                gateways={gateways}
                onSearchChange={setSearchTerm}
                onGatewayChange={(value) => setGatewayFilter(value as 'all' | string)}
            />

            <SwiftFlowErrorsTable
                errors={paginated}
                totalCount={filteredErrors.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                onEdit={handleOpenEdit}
            />

            <EditSwiftFlowErrorDialog
                open={Boolean(editState?.open)}
                editState={editState}
                isSaving={isSaving}
                onChange={setEditState}
                onSave={handleSave}
                onClose={handleCloseEdit}
            />

            <BulkUploadCsvDialog
                open={bulkDialogOpen}
                csvContent={csvContent}
                isImporting={isImporting}
                importLog={importLog}
                importSummary={importSummary}
                onCsvChange={setCsvContent}
                onFileChange={handleFileChange}
                onStart={handleStartImport}
                onClose={handleCloseBulkDialog}
            />

            <CreateSwiftFlowErrorDialog
                open={createDialogOpen}
                values={{
                    originalMessage: createOriginalMessage,
                    code: createCode,
                    gateway: createGateway,
                    unifiedMessage: createUnifiedMessage,
                    errorType: createErrorType,
                    notify: createNotify,
                }}
                isCreating={isCreating}
                onChange={{
                    setOriginalMessage: setCreateOriginalMessage,
                    setCode: setCreateCode,
                    setGateway: setCreateGateway,
                    setUnifiedMessage: setCreateUnifiedMessage,
                    setErrorType: setCreateErrorType,
                    setNotify: setCreateNotify,
                }}
                gateways={gateways}
                onSubmit={handleCreateSubmit}
                onClose={handleCloseCreateDialog}
            />
        </Box>
    );
}
